/* -------------------------------------
 * accordion for <details class="skb-details">
 * --------------------------------- */
class Accordion {
    constructor(el) {
      // Store the <details> element
      this.el = el;
      // Store the <summary> element
      this.summary = el.querySelector('summary');
      // Store the <div class="content"> element
      this.content = el.querySelector('.content');
    // Allow per-instance animation duration overrides
    this.duration = Number(this.el.dataset.accordionSpeed) || 300;

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    // Ensure the open state reflects on load when pre-opened
    if (this.el.open) {
      this.el.classList.add('open');
    }
      // Detect user clicks on the summary element
      this.summary.addEventListener('click', (e) => this.onClick(e));
    }

    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.el.style.overflow = 'hidden';
      this.el.classList.toggle('open');
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.el.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.el.open) {
        this.shrink();
      }
    }

    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.el.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.el.animate(
        {
          // Set the keyframes from the startHeight to endHeight
          height: [startHeight, endHeight],
        },
        {
          duration: this.duration,
          easing: 'ease-out',
        }
      );

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => (this.isClosing = false);
    }

    open() {
      // Apply a fixed height on the element
      this.el.style.height = `${this.el.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.el.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.el.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.el.animate(
        {
          // Set the keyframes from the startHeight to endHeight
          height: [startHeight, endHeight],
        },
        {
          duration: this.duration,
          easing: 'ease-out',
        }
      );
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => (this.isExpanding = false);
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.el.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.el.style.height = this.el.style.overflow = '';
    }
}


/* -------------------------------------
 * scrollable contents with progressbar
 * ----------------------------------- */
class ScrollContentsSkb {
    constructor(container) {
      this.container = container;
      this.list = container.querySelector('.skb_progressbar_contents');
      this.leftButton = container.querySelector('.skb_progressbar_action-left');
      this.rightButton = container.querySelector('.skb_progressbar_action-right');
      this.progressBar = container.querySelector('.skb_progressbar-width span');
  
      this.init();
    }
  
    init() {
      if (!this.list) return;
      this.checkScroll();
      this.addEventListeners();
      window.addEventListener('resize', () => this.checkScroll());
    }
  
    // Check if overflow-x scroll is true and add 'has-scroll' class
    checkScroll() {
      if (!this.list) return;
      if (this.list.scrollWidth > this.list.clientWidth) {
        this.list.classList.add('has-scroll');
      } else {
        this.list.classList.remove('has-scroll');
      }
      this.updateButtonStates();
      this.updateProgressBar();
    }
  
    // Get the scroll amount dynamically based on the first item's width
    getScrollAmount() {
      if (!this.list) return 300;
      const firstItem = this.list.querySelector('.skb_progressbar_contents-item');
      return firstItem ? firstItem.offsetWidth * 2 + parseInt(window.getComputedStyle(firstItem).marginRight) : 300;
    }
  
    // Update button states (enable/disable)
    updateButtonStates() {
      if (!this.list || !this.leftButton || !this.rightButton) return;
      this.leftButton.disabled = this.list.scrollLeft === 0;
      this.rightButton.disabled = this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth;
    }
  
  // Update the progress bar (cumulative covered fraction)
  updateProgressBar() {
    if (!this.list || !this.progressBar) return;
    const containerWidth = this.list.clientWidth;
    const totalContentWidth = this.list.scrollWidth;
    const scrolledAmount = this.list.scrollLeft;

    if (totalContentWidth === 0) {
      this.progressBar.style.width = '0%';
      this.progressBar.style.left = '0%';
      return;
    }

    const coveredFraction = Math.min(1, (scrolledAmount + containerWidth) / totalContentWidth);
    this.progressBar.style.width = `${coveredFraction * 100}%`;
    this.progressBar.style.left = '0%';
  }
  
    // Scroll handler for left and right buttons
    scrollList(direction) {
      if (!this.list) return;
      const scrollAmount = this.getScrollAmount();
      const newScrollPosition =
        direction === 'left'
          ? Math.max(0, this.list.scrollLeft - scrollAmount)
          : Math.min(this.list.scrollWidth - this.list.clientWidth, this.list.scrollLeft + scrollAmount);
  
      this.list.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth',
      });
    }
  
    // Add event listeners
    addEventListeners() {
      if (!this.list) return;
      if (this.leftButton) {
        this.leftButton.addEventListener('click', () => this.scrollList('left'));
      }
      if (this.rightButton) {
        this.rightButton.addEventListener('click', () => this.scrollList('right'));
      }
  
      this.list.addEventListener('scroll', () => {
        this.updateButtonStates();
        this.updateProgressBar();
      });
    }
}

/** ===========================
 * Initializing 
 * =========================== */
function initAccordion() {
 document.querySelectorAll('.skb-details').forEach((el) => {
    new Accordion(el);
 });
}

function initContentsProgress() {
    // Initialize progressbar containers for other sections (not ingredients)
    // Ingredients sections now have their own self-contained JavaScript
    const containers = document.querySelectorAll('.skb_progressbar-container:not(.skb-ingredients-container)');
    containers.forEach((container) => {
      new ScrollContentsSkb(container);
    });
}



function initToggleSKB() {
  /**
   * HTML structure for this 
   * <div class="skb_toggle-wrap">
   *  <div class="skb_toggle-btn"></div>
   *  <div class="skb_toggle-content"></div>
   * </div>
   */
  const infoWraps = document.querySelectorAll('.skb_toggle-wrap');

    infoWraps.forEach((wrap) => {
        // Get the h2 and skb_info_contents inside each skb_info_wrap
        const title = wrap.querySelector('.skb_toggle-btn');
        const contents = wrap.querySelector('.skb_toggle-content');

        if (title && contents) {
            // Add click event listener to the title
            title.addEventListener('click', () => {
                // Toggle the 'active' class on both the title and the contents
                title.classList.toggle('active');
                contents.classList.toggle('active');
            });
        }
    });
}

function initCardProductMetaVideo() {
    const videoContainers = document.querySelectorAll(".skb-has-video-meta");
  
    videoContainers.forEach(container => {
      const video = container.querySelector("video");
  
      container.addEventListener("mouseenter", () => {
        if (video) {
          video.play();
        }
      });
  
      container.addEventListener("mouseleave", () => {
        if (video) {
          video.pause();
          video.currentTime = 0; // Reset to the beginning
        }
      });
    });
}

  
/* ========================= 
 *  DOMContentLoaded
 * ========================= */
document.addEventListener("DOMContentLoaded", function() {
    initAccordion();
    initContentsProgress();
    initToggleSKB();
    initCardProductMetaVideo();
});


/* ========================= 
 *  Shopify Theme Editor
 * ========================= */
if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', function() {
        initAccordion();
        initContentsProgress();
        initToggleSKB();
        initCardProductMetaVideo();
    });
}